import { useEffect, useRef } from 'react';

// Haptik erst beim ersten Wischen laden: Das Modal haengt an fast jeder Seite,
// und viele Tests bilden @capacitor/core ohne registerPlugin nach — ein fester
// Import liesse sie schon beim Laden scheitern.
const buzz = (kind) => {
  import('../utils/haptics')
    .then(({ haptic }) => haptic[kind]?.())
    .catch(() => {});
};

/**
 * Bottom-Sheet nach unten wegwischen (Oktober 2026, Profilblatt).
 *
 * Der Greifer oben am Blatt verspricht, dass man es nach unten ziehen kann —
 * bis hierher ging das aber nur ueber das X oder einen Tipp auf den Schleier.
 * Der Hook haengt Touch-Listener an das <dialog> und schiebt es beim Ziehen
 * per transform mit dem Finger mit.
 *
 * - Zieht nur, wenn die Geste nach unten und eher senkrecht beginnt.
 * - Zieht NICHT, wenn unter dem Finger etwas schon gescrollt ist (dann gehoert
 *   die Geste dem Scrollen) oder ein Eingabefeld liegt. `data-sheet-no-swipe`
 *   nimmt weitere Bereiche aus.
 * - Haptik: ein Tick, sobald Loslassen schliessen wuerde (und wieder, wenn man
 *   zurueckzieht). So spuert man die Schwelle, ohne hinzusehen.
 * - Schliesst bei genug Weg ODER einem schnellen Wisch, sonst federt es zurueck.
 * - Nur auf dem Telefon (isActive), am Desktop ist das Blatt eine Karte.
 */

const START_SLOP_PX = 8;
const CLOSE_MIN_PX = 90;
const CLOSE_FRACTION = 0.25;
const FLING_PX_PER_MS = 0.6;
const FLING_MIN_PX = 40;
const CLOSE_MS = 200;
const NO_SWIPE = 'input, textarea, select, [contenteditable="true"], [data-sheet-no-swipe]';

const prefersReducedMotion = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const hasScrolledAncestor = (target, root) => {
  for (let el = target; el && el !== root; el = el.parentElement) {
    if (el.scrollTop > 0) return true;
  }
  return root ? root.scrollTop > 0 : false;
};

const always = () => true;

export default function useSheetSwipe(ref, { enabled, onClose, isActive = always }) {
  // onClose aendert sich bei jedem Render des Aufrufers — die Listener sollen
  // deshalb nicht neu angehaengt werden, sondern immer die aktuelle Fassung rufen.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const sheet = ref.current;
    if (!enabled || !sheet) return undefined;

    let drag = null;
    let closeTimer = null;

    const reset = () => {
      sheet.style.transition = 'transform 250ms cubic-bezier(0.2, 0.8, 0.2, 1)';
      sheet.style.transform = '';
    };

    const onStart = (e) => {
      drag = null;
      if (e.touches.length !== 1 || !isActive()) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest(NO_SWIPE)) return;
      if (hasScrolledAncestor(target, sheet)) return;
      const t = e.touches[0];
      const now = Date.now();
      drag = {
        x: t.clientX, y: t.clientY, phase: 'pending', dy: 0, armed: false, lastY: t.clientY, lastT: now, vy: 0,
      };
    };

    const onMove = (e) => {
      if (!drag) return;
      const t = e.touches[0];
      const dx = t.clientX - drag.x;
      const dy = t.clientY - drag.y;

      if (drag.phase === 'pending') {
        if (Math.abs(dx) < START_SLOP_PX && Math.abs(dy) < START_SLOP_PX) return;
        // Nach oben oder seitwaerts: gehoert dem Inhalt (Scrollen, Reiter).
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) { drag = null; return; }
        drag.phase = 'drag';
        sheet.style.transition = 'none';
      }

      // Sonst zieht iOS den Inhalt mit (Gummiband) statt das Blatt.
      if (e.cancelable) e.preventDefault();

      const now = Date.now();
      const dt = now - drag.lastT;
      if (dt > 0) drag.vy = (t.clientY - drag.lastY) / dt;
      drag.lastY = t.clientY;
      drag.lastT = now;
      drag.dy = Math.max(0, dy);
      sheet.style.transform = `translateY(${drag.dy}px)`;

      const threshold = Math.max(CLOSE_MIN_PX, sheet.offsetHeight * CLOSE_FRACTION);
      const armed = drag.dy >= threshold;
      if (armed !== drag.armed) {
        drag.armed = armed;
        buzz('selection');
      }
    };

    const onEnd = (e) => {
      const current = drag;
      drag = null;
      if (!current || current.phase !== 'drag') return;
      const fling = current.vy > FLING_PX_PER_MS && current.dy > FLING_MIN_PX;
      const shouldClose = e.type !== 'touchcancel' && (current.armed || fling);
      if (!shouldClose) { reset(); return; }

      if (!current.armed) buzz('light');
      if (prefersReducedMotion()) { onCloseRef.current?.(); return; }
      sheet.style.transition = `transform ${CLOSE_MS}ms ease-in`;
      sheet.style.transform = 'translateY(100%)';
      closeTimer = setTimeout(() => onCloseRef.current?.(), CLOSE_MS);
    };

    sheet.addEventListener('touchstart', onStart, { passive: true });
    sheet.addEventListener('touchmove', onMove, { passive: false });
    sheet.addEventListener('touchend', onEnd);
    sheet.addEventListener('touchcancel', onEnd);
    return () => {
      clearTimeout(closeTimer);
      sheet.removeEventListener('touchstart', onStart);
      sheet.removeEventListener('touchmove', onMove);
      sheet.removeEventListener('touchend', onEnd);
      sheet.removeEventListener('touchcancel', onEnd);
    };
  }, [ref, enabled, isActive]);
}
